'use client';
import { useState, useEffect } from 'react';

interface Ranking {
  name: string;
  rate: number;
  services: number;
  streak: number;
  rankChange: number; // positive = up, negative = down, 0 = same
}

type Period = 'today' | 'week' | 'month' | 'alltime';
type GroupBy = 'leader' | 'branch' | 'tech';

export default function Leaderboard() {
  const [period, setPeriod] = useState<Period>('today');
  const [groupBy, setGroupBy] = useState<GroupBy>('leader');
  const [rankings, setRankings] = useState<Ranking[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    fetch(`/api/dashboard/leaderboard?period=${period}&groupBy=${groupBy}`, { credentials: 'include' })
      .then(r => r.json())
      .then(d => setRankings(d.rankings || []))
      .catch(() => setRankings([]))
      .finally(() => setLoading(false));
  }, [period, groupBy]);

  const trophy = (rate: number) => rate >= 100 ? '🥇' : rate >= 95 ? '🥈' : rate >= 90 ? '🥉' : '';
  const rankIcon = (change: number) => change > 0 ? <span className="text-green-500 text-xs">↑{change}</span> : change < 0 ? <span className="text-red-500 text-xs">↓{Math.abs(change)}</span> : <span className="text-gray-400 text-xs">—</span>;

  const periodOptions: { key: Period; label: string }[] = [
    { key: 'today', label: 'Today' }, { key: 'week', label: 'Week' }, { key: 'month', label: 'Month' }, { key: 'alltime', label: 'All Time' },
  ];
  const groupOptions: { key: GroupBy; label: string }[] = [
    { key: 'leader', label: 'By Leader' }, { key: 'branch', label: 'By Branch' }, { key: 'tech', label: 'By Tech' },
  ];

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
      <div className="px-5 py-4 border-b border-gray-100">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <h2 className="font-semibold text-gray-800">🏆 Leader Rankings</h2>
          <div className="flex gap-1">
            {periodOptions.map(p => (
              <button key={p.key} onClick={() => setPeriod(p.key)} className={`px-2.5 py-1 rounded text-xs font-medium ${period === p.key ? 'bg-[#D86018] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>{p.label}</button>
            ))}
          </div>
        </div>
        <div className="flex gap-1 mt-2">
          {groupOptions.map(g => (
            <button key={g.key} onClick={() => setGroupBy(g.key)} className={`px-2.5 py-1 rounded text-xs font-medium ${groupBy === g.key ? 'bg-[#01AA90] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>{g.label}</button>
          ))}
        </div>
      </div>
      <div className="max-h-[400px] overflow-y-auto">
        {loading ? (
          <div className="p-5 space-y-3">
            {[1, 2, 3, 4].map(i => <div key={i} className="h-12 bg-gray-100 rounded animate-pulse" />)}
          </div>
        ) : rankings.length === 0 ? (
          <div className="p-8 text-center text-gray-400">No data available</div>
        ) : (
          <div className="divide-y divide-gray-50">
            {rankings.map((r, i) => (
              <div key={r.name} className={`px-5 py-3 flex items-center gap-3 hover:bg-gray-50 ${i < 3 ? 'bg-yellow-50/30' : ''}`}>
                <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${i === 0 ? 'bg-yellow-100 text-yellow-700' : i === 1 ? 'bg-gray-100 text-gray-600' : i === 2 ? 'bg-orange-100 text-orange-600' : 'bg-gray-50 text-gray-500'}`}>
                  {i + 1}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-gray-800 text-sm flex items-center gap-1">
                    {trophy(r.rate)} {r.name}
                  </div>
                  <div className="text-xs text-gray-500">{r.services} services · {r.streak > 0 ? `🔥 ${r.streak} day streak` : 'No streak'}</div>
                </div>
                <div className="text-right">
                  <div className={`font-bold text-sm ${r.rate >= 90 ? 'text-green-600' : r.rate >= 70 ? 'text-orange-500' : 'text-red-500'}`}>{Math.round(r.rate)}%</div>
                  {rankIcon(r.rankChange)}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
